import {
    Component,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged, take } from 'rxjs';

import { DocumentService } from '@services/api/document/document.service';
import { SnackbarService } from '@services/snackbar.service';
import { UserService } from '@services/api/user/user.service';
import { WorkflowService } from '@services/api/workflow/workflow.service';
import { WorkflowDataService } from '@services/data/workflow-data.service';
import { TooltipComponent } from '@shared/tooltip/tooltip.component';
import { ProgressBarComponent } from '@shared/progress-bar/progress-bar.component';
import { ContentFieldComponent } from '@shared/content-field/content-field.component';
import { PriorityFieldComponent } from '@features/priority-field/priority-field.component';
import { PrioritySelectComponent } from '@shared/select/priority-select/priority-select.component';
import { FlatButtonComponent } from '@shared/button/flat-button/flat-button.component';
import { PublishDocumentDialogComponent } from '@main/dialogs/publish-document-dialog/publish-document-dialog.component';
import { CollaboratorsCollapsibleComponent } from '@main/header-document-details/collaborators-collapsible/collaborators-collapsible.component';
import { FeatureFlagDirective } from '@directives/feature-flag.directive';
import { SubscriptionList, SubscriptionListType } from '@helpers/subscription';
import { convertToLocalTimeAndFormat } from '@helpers/time';
import { WorkflowDocument } from '@models/document';
import {
    Priority,
    priorityMap,
    priorityOptions,
} from '@models/shared/priority';
import DATA_QA from '@automation/data-qa.json';

@Component({
    selector: 'app-header-document-details',
    templateUrl: 'header-document-details.component.html',
    styleUrl: 'header-document-details.component.scss',
    imports: [
        MatIconModule,
        ReactiveFormsModule,
        ContentFieldComponent,
        TooltipComponent,
        ProgressBarComponent,
        PrioritySelectComponent,
        PriorityFieldComponent,
        FlatButtonComponent,
        CollaboratorsCollapsibleComponent,
        FeatureFlagDirective,
    ],
})
export class HeaderDocumentDetailsComponent
    implements OnInit, OnChanges, OnDestroy
{
    [x: string]: any;
    @Input({ required: true }) document: WorkflowDocument | null = null;
    @Input() mode: 'summary' | 'detail' = 'summary';
    @Input() isAdmin: boolean = false;

    allAnalystsAreDocumentCollaborators = false;

    form!: FormGroup;
    formChanged: boolean = false;
    progressValue = 0;
    prioOptions = structuredClone(priorityOptions);
    private _subscriptions = new SubscriptionList() as SubscriptionListType;
    readonly dataQa = DATA_QA;

    private readonly dialog = inject(MatDialog);
    private readonly userService = inject(UserService);
    private readonly formBuilder = inject(FormBuilder);
    private readonly snackbarService = inject(SnackbarService);
    private readonly documentService = inject(DocumentService);
    private readonly workflowService = inject(WorkflowService);
    private readonly workflowDataService = inject(WorkflowDataService);

    get allCompleted() {
        return (
            this.document &&
            this.document.progress.completed === this.document.progress.total
        );
    }

    ngOnInit() {
        this.setUserPermission();
        this.initForm();
        this.handleFormChange();
        this.subscribeWorkflows();
    }

    ngOnChanges(changes: SimpleChanges) {
        this.progressValue = 0;

        const documentChange = changes['document'];
        if (documentChange?.currentValue?.progress) {
            const currentProgress = documentChange?.currentValue?.progress;
            this.progressValue =
                (currentProgress.completed * 100) / currentProgress.total;
        }

        if (documentChange?.currentValue?.workflowId) {
            this.workflowDataService.workflows$
                .pipe(take(1))
                .subscribe((workflows) =>
                    this.evaluateCollaboratorPermission(workflows),
                );
        }
    }

    ngOnDestroy() {
        this._subscriptions.unsubscribeAllSafe();
    }

    protected openPublishDocumentModal() {
        if (this.document) {
            this.dialog.open(PublishDocumentDialogComponent, {
                data: {
                    id: this.document.id,
                    name: this.document.name,
                },
                maxWidth: '448px',
                minWidth: '200px',
                width: '448px',
            });
        }
    }

    private initForm() {
        this.form = this.formBuilder.group({
            priority: new FormControl(this.document?.priority),
        });
    }

    private handleFormChange() {
        this._subscriptions['form-value-change'] = this.form.valueChanges
            .pipe(debounceTime(250))
            .pipe(distinctUntilChanged())
            .subscribe(() => this.updateDocumentPriority());
    }

    private updateDocumentPriority() {
        if (this.document) {
            this._subscriptions['update-document'] = this.documentService
                .updateDocument(this.document.id, this.form.value)
                .subscribe({
                    next: (res) => {
                        if (res) {
                            this.snackbarService.success({
                                header: `Document priority changed to ${priorityMap[this.form.value.priority as Priority].text}`,
                            });
                        } else {
                            this._subscriptions.unsubscribeAllSafe();
                            this.snackbarService.error({
                                header: 'Document priority could not be changed',
                            });
                            this.form.patchValue({
                                priority: this.document!.priority,
                            });
                            this.handleFormChange();
                        }
                    },
                });
        }
    }

    private setUserPermission() {
        this.userService.isAdminUser().subscribe({
            next: (isAdmin) => (this.isAdmin = isAdmin),
        });
    }

    private subscribeWorkflows(): void {
        this._subscriptions['workflows'] =
            this.workflowDataService.workflows$.subscribe({
                next: (workflows) =>
                    this.evaluateCollaboratorPermission(workflows),
            });

        this._subscriptions['refresh-workflows'] = this.workflowService
            .getWorkflows()
            .subscribe();
    }

    private evaluateCollaboratorPermission(
        workflows: {
            id: number;
            allAnalystsAreDocumentCollaborators: boolean;
        }[],
    ): void {
        const workflowId = this.document?.workflowId;
        if (workflowId) {
            const workflow = workflows.find((w) => w.id === workflowId);
            this.allAnalystsAreDocumentCollaborators =
                workflow?.allAnalystsAreDocumentCollaborators ?? false;
        }
    }

    getFormattedDate(date: string): string {
        return convertToLocalTimeAndFormat(date);
    }
}
